import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { instalar, marcarRecusa, ondeInstala } from '../utils/instalarApp';
import './ModalDeInstalar.css';

/**
 * O convite para instalar o app no celular.
 *
 * ── Duas telas, porque são duas realidades ────────────────────────────────
 *
 * No Android o navegador entrega um evento que abre a instalação de verdade:
 * um toque e acabou. No iPhone o Safari não implementa nada disso — instalar é
 * Compartilhar → "Adicionar à Tela de Início", e o único caminho honesto é
 * ENSINAR.
 *
 * Por isso não há um botão "Instalar" único com um `if` dentro. Um botão que
 * não instala é pior que uma instrução clara: ele promete uma ação e devolve
 * um texto.
 *
 * ── O que este modal NÃO faz ─────────────────────────────────────────────
 *
 * Abrir o app sozinho depois de instalar. Não existe API na web para lançar um
 * app instalado — seria uma página poder abrir programas no aparelho de quem a
 * visita. Depois de instalar, quem abre é a pessoa, tocando no ícone. O texto
 * diz isso em vez de deixar a expectativa no ar.
 */

const ModalDeInstalar: React.FC<{
  /** `true` no iPhone, onde só dá para ensinar. */
  ensinando: boolean;
  /**
   * Fechar conta como recusa?
   *
   * `true` quando o modal apareceu sozinho: fechar é dizer não, e o convite
   * some por 30 dias.
   *
   * `false` quando ELA abriu pelo menu. Aí fechar é só fechar — punir com 30
   * dias de silêncio quem pediu para ver seria ler "mudei de ideia" como
   * "nunca mais me mostre".
   */
  contaComoRecusa: boolean;
  onFechar: () => void;
}> = ({ ensinando, contaComoRecusa, onFechar }) => {
  const [ocupado, setOcupado] = useState(false);
  // O app instala no celular e no computador; o texto diz qual dos dois.
  const aparelho = ondeInstala();
  const caixa = useRef<HTMLDivElement>(null);

  useTravarScroll();
  useFocoNoModal(caixa);
  /* O voltar do celular fecha isto antes de sair do app. */
  useFechavelComVoltar(true, onFechar);

  /*
   * Fechar é RECUSAR, por qualquer caminho — o ✕, o toque fora, o Esc, o
   * voltar do celular.
   *
   * Sem isso, fechar pelo lado de fora não marcaria nada e o convite voltaria
   * no próximo login. É assim que um convite vira um pop-up.
   */
  const recusar = React.useCallback(() => {
    if (contaComoRecusa) marcarRecusa();
    onFechar();
  }, [onFechar, contaComoRecusa]);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') recusar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [recusar]);

  const aoInstalar = async () => {
    setOcupado(true);
    const escolha = await instalar();
    /*
     * Recusar na caixa do navegador conta como recusa aqui também. Ela já disse
     * não uma vez; insistir no próximo login é não ter ouvido.
     */
    if (escolha !== 'accepted' && contaComoRecusa) marcarRecusa();
    onFechar();
  };

  const corpo = (
    <div className="mi-veu" onClick={recusar} ref={caixa}>
      <div
        className="mi-caixa"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mi-titulo"
      >
        <button className="mi-fechar" onClick={recusar} aria-label="Agora não">✕</button>

        <img className="mi-icone" src={`${process.env.PUBLIC_URL}/icons/favicon.svg`}
             alt="" aria-hidden="true" width={64} height={64} />

        <h2 className="mi-titulo" id="mi-titulo">
          {aparelho === 'celular'
            ? 'Deixe o FinTracking na sua tela inicial'
            : 'Deixe o FinTracking na sua área de trabalho'}
        </h2>

        {/*
          Uma linha, e só.

          Eram duas frases explicando tela cheia, barra do navegador e espaço em
          disco — o relato foi "muito grande". Num convite, texto longo é o que
          se pula para achar o botão, e o que se pula não informa.

          O que ela precisa saber para decidir é o que o app vira, não como ele
          funciona por dentro. Quem quiser os detalhes descobre instalando.
        */}
        <p className="mi-texto">Ele fica como um app no seu {aparelho}.</p>

        {ensinando ? (
          <>
            {/*
              No iPhone o passo a passo É a funcionalidade. Ele precisa citar o
              ícone que ela vai procurar, não só o nome do menu — "Compartilhar"
              sem o desenho manda procurar a palavra numa barra que só tem
              símbolos.
            */}
            <ol className="mi-passos">
              <li>
                Toque em <strong>Compartilhar</strong>, o quadrado com a seta
                para cima, na barra de baixo do Safari.
              </li>
              <li>
                Role e escolha <strong>Adicionar à Tela de Início</strong>.
              </li>
              <li>Confirme em <strong>Adicionar</strong>.</li>
            </ol>
            <p className="mi-nota">
              O iPhone não deixa um site fazer isso sozinho, por isso os passos
              em vez de um botão.
            </p>
            {/*
              Um botão só aqui.
              
              A primeira versão tinha "Entendi" e "Agora não" juntos, e os dois
              faziam exatamente a mesma coisa: fechar. Duas saídas idênticas com
              nomes diferentes fazem a pessoa procurar a diferença que não
              existe.
              
              No Android há de fato duas ações — instalar e adiar — e por isso
              lá são dois. Aqui não há o que aceitar: o modal ensinou, e sair
              dele é a única coisa a fazer.
            */}
            <button className="mi-btn mi-btn-principal" onClick={recusar}>Entendi</button>
          </>
        ) : (
          <>
            <button className="mi-btn mi-btn-principal" onClick={aoInstalar} disabled={ocupado} aria-busy={ocupado}>
              Instalar
            </button>
            {/*
              Dito antes de acontecer: o navegador vai abrir a caixa DELE por
              cima, e sem aviso isso parece que o app travou.
            */}
            <p className="mi-nota">
              O navegador vai pedir a confirmação. Depois de instalar, o ícone
              aparece {aparelho === 'celular' ? 'na sua tela inicial' : 'entre os seus aplicativos'}
              , e é por ele que você abre.
            </p>
          </>
        )}

        {!ensinando && (
          <button className="mi-btn mi-btn-fantasma" onClick={recusar}>
            {contaComoRecusa ? 'Agora não' : 'Fechar'}
          </button>
        )}
      </div>
    </div>
  );

  // Portal: o mesmo motivo dos outros modais — o cabeçalho cria contexto de
  // empilhamento, e sem escapar dele o modal ficaria preso dentro.
  return createPortal(corpo, document.body);
};

export default ModalDeInstalar;
